import './../style.scss';
import './BriefContract.scss';
import {
  formatCash,
  formatDate,
  calcPlatformFee,
  calcSponsorServiceFee,
  calcSponsorVat,
  calcTotalPayableBySponsor,
  calcProfessionalFee,
  findSelectedProfessional,
  findQuoteByProfessional,
  financialCalculations
} from './../../../../utils/sharedFunctions.js';
import { useState, useEffect, useMemo } from 'react';
import { useStore } from '../../../../stores';

const BriefContract = ({ project }) => {
  const { uiStore } = useStore();
  const [selectedBrief, setSelectedBrief] = useState(project);

  useEffect(() => {
    setSelectedBrief(project);
  }, [project]);

  const contractData = useMemo(() => {
    const isMigrated = selectedBrief?.isMigrated === true;

    // Default return object structure
    const result = {
      isMigrated,
      description: selectedBrief?.brief?.briefDescription ?? selectedBrief?.briefDescription ?? 'No description provided.',
      startDate: selectedBrief?.brief?.projectPlan?.[0]?.projStartDate ?? selectedBrief?.projectStartDate,
      endDate: selectedBrief?.brief?.projectPlan?.[0]?.projEndDate ?? selectedBrief?.projectEndDate,
      serviceCategories: [],
      skills: [],
      milestones: [],
      deliverables: [],
      
      rawProfessionalFee: 0,
      formatted: {
        professionalFee: "0,00",
        credFee: "0,00",
        receivable: "0,00",
        totalProjectCost: "0,00",
        sponsorFee: "0,00",
        sponsorVat: "0,00",
        totalSponsor: "0,00",
        clientSubTotal: "0,00",
        clientVat: "0,00",
        totalClient: "0,00",
        credFee12: "0,00",
        voucherDebit: "0,00"
      }
    };

    try {
      if (isMigrated) {
        const sv = selectedBrief?.staticValues || {};
        result.rawProfessionalFee = sv.professionalFees || 0;
        
        // Static Values directly mapping to UI needs
        result.formatted.professionalFee = formatCash(sv.professionalFees || 0);
        result.formatted.credFee = formatCash(sv.credippleFee || 0);
        result.formatted.totalProjectCost = formatCash(sv.jobAmount || 0);
        
        result.formatted.receivable = formatCash(sv.professionalFees || 0);
        
        result.formatted.clientSubTotal = formatCash(sv.jobAmount || 0);
        result.formatted.totalClient = formatCash(sv.jobAmount || 0); 
        
        result.serviceCategories = selectedBrief?.serviceCategory || [];
        result.skills = []; 
        return result;
      }

      
      const shortlist = selectedBrief?.brief?.shortlist ?? selectedBrief?.shortlist;
      let selectedProfessional = findSelectedProfessional(shortlist);
      
      // Fallback: If no "Selected" status, use the assigned professional
      if (!selectedProfessional && selectedBrief?.professional) {
        selectedProfessional = { professional: selectedBrief.professional };
      }

      const quoteSource = selectedBrief?.brief?.quote ?? selectedBrief?.quote ?? [];
      let quoteByProfessional = findQuoteByProfessional(quoteSource, selectedProfessional?.professional?._id);

      // Fallback: If utility returns null, just grab first quote available (common in single-pro projects)
      if (!quoteByProfessional && quoteSource.length > 0) {
        quoteByProfessional = quoteSource[0];
      }

      result.serviceCategories = selectedBrief?.typeOfProfessional || selectedBrief?.serviceCategory || [];
      result.skills = selectedBrief?.skillsOfProfessional || [];
      result.milestones = quoteSource.filter(q => q?.milestone).map(q => q.milestone);
      result.deliverables = selectedBrief?.brief?.projectPlan?.[0]?.deliverables || selectedBrief?.projectPlan?.[0]?.deliverables || [];

      // 3. Financial Calculations
      const rawProfFee = calcProfessionalFee(quoteByProfessional?.billables);
      result.rawProfessionalFee = rawProfFee;
      
      const finances = financialCalculations(rawProfFee, selectedBrief);
      
      // Sponsor Calcs (If applicable)
      const rawSponsorFee = calcSponsorServiceFee(rawProfFee);
      const rawSponsorVat = calcSponsorVat(rawProfFee);
      const rawTotalSponsor = calcTotalPayableBySponsor(rawProfFee);
      // If your util `calcTotalPayableBySponsor` handles logic internally, use it. Otherwise rely on `finances.SponsorSubTot`.

      // --- FORMATTING VALUES ---
      result.formatted.professionalFee = formatCash(rawProfFee);
      
      // Credipple Fee (10% or Minimum)
      const credFeeVal = Number(rawProfFee) < 2500 ? 250 : (finances.Remaining2BPayByClient * 0.1);
      result.formatted.credFee = formatCash(credFeeVal);
      
      const totalCostVal = rawProfFee < 2500 ? (rawProfFee + 250) : (finances.Remaining2BPayByClient + finances.Remaining2BPayByClient * 0.1);
      result.formatted.totalProjectCost = formatCash(totalCostVal);
      
      const receivableVal = rawProfFee < 2500 ? (rawProfFee + 250) : (finances.Remaining2BPayByClient - finances.Remaining2BPayByClient * 0.1);
      result.formatted.receivable = formatCash(receivableVal);

      // Sponsor Display
      result.formatted.sponsorFee = formatCash(rawSponsorFee);
      result.formatted.sponsorVat = formatCash(rawSponsorVat);
      result.formatted.totalSponsor = formatCash(finances.SponsorSubTot * 1.15); 

      // Client Display
      result.formatted.voucherDebit = formatCash(finances.voucherAmntDebited);
      result.formatted.credFee12 = formatCash(finances.CredFee12);
      
      const clientSub = rawProfFee < 2500 ? (rawProfFee + 250) : finances.ClientSubTot;
      result.formatted.clientSubTotal = formatCash(clientSub);
      
      const clientVat = rawProfFee < 2500 ? (clientSub * 0.15) : (finances.ClientSubTot * 0.15);
      result.formatted.clientVat = formatCash(clientVat);
      
      const totalClient = rawProfFee < 2500 ? (clientSub * 1.15) : (finances.ClientSubTot * 1.15);
      result.formatted.totalClient = formatCash(totalClient);

    } catch (err) {
      console.warn("Contract Calc Error:", err);
    }

    return result;

  }, [selectedBrief]);

  return (
    <div className='brief-contract'>
      <div className='summary-info'>
        <div className='summary-header'>
          <h2>Project Summary</h2>
        </div>
        <div className='summary-text'>
          <p>Please note that this is just a summary, but you can find the full version of your contract here.</p>
        </div>

        <div className='row'>
          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Total Project Cost</h2>
            </div>
            <div className='value'>
              {`ZAR ${contractData.formatted.totalProjectCost}`}
            </div>
          </div>

          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Credipple Fee</h2>
            </div>
            <div className='value'>
              {`ZAR ${contractData.formatted.credFee}`}
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Category of Service</h2>
            </div>
            <div className='value'>
              {contractData.serviceCategories.length > 0 ? (
                 contractData.serviceCategories.map((cat, index) => <div key={index}>{cat}</div>)
              ) : <div>No Data</div>}
            </div>
          </div>

          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Skills of Professional</h2>
            </div>
            <div className='value'>
              {contractData.skills.length > 0 ? (
                 contractData.skills.map((skill, index) => <div key={index}>{skill}</div>)
              ) : <div>No Data</div>}
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Project Start Date</h2>
            </div>
            <div className='value'>
              {formatDate(contractData.startDate)}
            </div>
          </div>

          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Project End Date</h2>
            </div>
            <div className='value'>
               {formatDate(contractData.endDate)}
            </div>
          </div>
        </div>

        <div className='milestone-grid'>
           {contractData.isMigrated ? (
               <div className='milestone-value-holder'><div className='value'>No Milestones (Migrated)</div></div>
           ) : (
             contractData.milestones.length > 0 ? (
                contractData.milestones.map((ms, index) => (
                    <div className='milestone-value-holder' key={index}>
                    <div className='summary-header'>
                        <h2>Milestone {index + 1}</h2>
                    </div>
                    <div className='value'>{ms}</div>
                    </div>
                ))
             ) : "No Milestones"
           )}
        </div>

        <div className='row descriptions-holder'>
          <div className='description-holder'>
            <div className='summary-header'>
              <h2>Description</h2>
            </div>
            <div className='text-info'>{contractData.description}</div>
          </div>
          <div className='deliverables-holder'>
            <div className='summary-header'>
              <h2>Deliverables & Responsibilities</h2>
            </div>
            <div className='text-info'>
               {contractData.isMigrated ? (
                   <span>Detailed deliverables not available for historical data.</span>
               ) : (
                  contractData.deliverables.length > 0 ? (
                    contractData.deliverables.map((del, index) => (
                        del?.nameOfItem ? <div style={{marginBottom:"10px"}} key={index}>{index+1}. {del.nameOfItem} <br /></div> : null
                    ))
                  ) : <span>No deliverables provided.</span>
               )}
            </div>
          </div>
        </div>
      </div>
      
      <div className='outer-quotes-info'>
        <div className='summary-header'>
          <h2>Quote for Project</h2>
        </div>

        <div className='quotes-holder'>
          <div className='quote'>
            <div className='summary-header'>
              <h2>Amount Payable to Professional:</h2>
            </div>

            <div className='quote-info'>
              <div className='quote-data'>
                <div className='quote-data-name'>Professional service fee:</div>
                <div className='quote-data-value'>{`ZAR ${contractData.formatted.professionalFee}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>Credipple platform fee (10%):</div>
                <div className='quote-data-value'>{`ZAR ${contractData.formatted.credFee}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-total'>Total amount Receivable:</div>
                 {`ZAR ${contractData.formatted.receivable}`}
                </div>
            </div>
          </div>

          <div className='seperator'></div>

          {/* VOUCHER SECTION (Only show if calculated value > 0) */}
          {parseFloat(contractData.formatted.voucherDebit.replace(/\s/g, '').replace(',', '.')) > 0 && (
            <div className='quote'>
              <div className='summary-header'>
                <h2>Amount to be invoiced to Sponsor:</h2>
              </div>

              <div className='quote-info'>
                <div className='quote-data'>
                  <div className='quote-data-name'>Professional service fee:</div>
                  <div className='quote-data-value'>{`ZAR ${contractData.formatted.professionalFee}`}</div>
                </div>

                <div className='quote-data'>
                  <div className='quote-data-name'>Credipple service fee (15%):</div>
                  <div className='quote-data-value'>{`ZAR ${contractData.formatted.sponsorFee}`}</div>
                </div>

                <div className='quote-data'>
                  <div className='quote-data-name'>VAT (15%):</div>
                  <div className='quote-data-value'>{`ZAR ${contractData.formatted.sponsorVat}`}</div>
                </div>

                <div className='quote-data'>
                  <div className='quote-data-total'>Total amount payable by Sponsor:</div>
                  <div className='quote-data-total-value'>{`ZAR ${contractData.formatted.totalSponsor}`}</div>
                </div>
              </div>
            </div>
          )}
          
          <div className='seperator'></div>

          <div className='quote'>
            <div className='summary-header'>
              <h2>Amount to be invoiced to Client:</h2>
            </div>

            <div className='quote-info'>
              <div className='quote-data'>
                <div className='quote-data-name'>Professional service fee:</div>
                <div className='quote-data-value'>{`ZAR ${contractData.formatted.professionalFee}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>Voucher amount debited</div>
                <div className='quote-data-value'>{`ZAR ${contractData.formatted.voucherDebit}`}</div>
              </div>

              {/* Only show 'Remaining' logic if not migrated (Migrated simplifies this) */}
              {!contractData.isMigrated && (
                 <div className='quote-data'>
                    <div className='quote-data-name'>Remaining balance due:</div>
                    {/* Using Client Subtotal as proxy for remaining balance in simple view */}
                    <div className='quote-data-value'>{`ZAR ${contractData.formatted.clientSubTotal}`}</div>
                 </div>
              )}

              <div className='quote-data'>
                <div className='quote-data-name'>Credipple Platform fee (10-12%):</div>
                <div className='quote-data-value'>{`ZAR ${contractData.formatted.credFee12}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>Subtotal</div>
                <div className='quote-data-value'>{`ZAR ${contractData.formatted.clientSubTotal}`}</div>
              </div>

              {!contractData.isMigrated && (
                  <div className='quote-data'>
                    <div className='quote-data-name'>VAT (15%):</div>
                    <div className='quote-data-value'>{`ZAR ${contractData.formatted.clientVat}`}</div>
                  </div>
              )}

              <div className='quote-data'>
                <div className='quote-data-total'>Total amount payable by Client:</div>
                <div className='quote-data-total-value'>{`ZAR ${contractData.formatted.totalClient}`}</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BriefContract;